import type {
  IHelpdeskAttachment,
  IHelpdeskDownloadUrl,
  IHelpdeskLimits,
  IHelpdeskMessage,
  IHelpdeskTicketContext,
  IHelpdeskTicketDetail
} from "@kuika/kuika-cl-model";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import HelpdeskComposer from "./helpdesk-composer";
import { readHelpdeskError } from "./helpdesk-errors";
import { IHelpdeskFileTotals, totalsOf } from "./helpdesk-files";
import HelpdeskMessageThread from "./helpdesk-message-thread";
import HelpdeskRestrictedNotice from "./helpdesk-restricted-notice";
import HelpdeskStatusBadge from "./helpdesk-status-badge";
import HelpdeskSurvey from "./helpdesk-survey";
import { describePendingReply, IHelpdeskPendingReply, isReplyInThread, pendingReplyMessage } from "./helpdesk-thread";
import HelpdeskTicketSkeleton from "./helpdesk-ticket-skeleton";
import { formatTicketDate } from "./helpdesk-tickets";

export interface HelpdeskTicketViewerProps {
  ticketNumber: number;
  limits: IHelpdeskLimits;
  /** Called after the ticket was marked read or changed, so the list and the badge refresh. */
  onTicketChanged: () => void;
  /** Bumped when the helpdesk stream reports a change to this ticket: re-read it in place. */
  refreshKey?: number;
  /** The ticket does not exist or is not visible to this person (404). */
  onNotFound?: () => void;
  /**
   * How long the first load waits before its skeleton appears. 0 when this load continues a wait
   * whose skeleton is already on screen, so the two read as one instead of blinking in between.
   */
  skeletonDelayMs?: number;
}

/** How close to the bottom still counts as reading the latest message. */
const FOLLOW_LATEST_THRESHOLD_PX: number = 48;

function contextLine(context: IHelpdeskTicketContext | null | undefined): string {
  return [context?.domain, context?.appName, context?.screenName].filter(Boolean).join(" / ");
}

const HelpdeskTicketViewer = ({
  ticketNumber,
  limits,
  onTicketChanged,
  refreshKey = 0,
  onNotFound,
  skeletonDelayMs
}: HelpdeskTicketViewerProps): React.ReactElement => {
  const [ticket, setTicket] = useState<IHelpdeskTicketDetail | null>(null);
  const [messages, setMessages] = useState<IHelpdeskMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [restricted, setRestricted] = useState<boolean>(false);
  // The reply being sent, drawn at the end of the thread until a re-read brings the real message.
  const [pendingReply, setPendingReply] = useState<IHelpdeskPendingReply | null>(null);
  // A refresh of a ticket already on screen keeps it there; only the first load shows the skeleton.
  const presentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading,
    hasData: ticket !== null,
    delayMs: skeletonDelayMs
  });
  // The survey sits under the whole thread; the header's prompt brings it into view.
  const surveyRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  // The conversation follows its newest message, as a chat does: on opening, on one's own reply, and
  // when a message arrives while the reader is at the bottom. Scrolled up into the history, it stays.
  const scrollRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const followsLatestRef: React.MutableRefObject<boolean> = useRef<boolean>(true);
  const latestKey: string = `${messages.length}:${messages[messages.length - 1]?.id ?? ""}:${pendingReply ? "pending" : ""}`;
  useLayoutEffect((): void => {
    const scroller: HTMLDivElement | null = scrollRef.current;
    if (scroller && followsLatestRef.current) scroller.scrollTop = scroller.scrollHeight;
  }, [latestKey, presentation]);
  // Kept in a ref so a parent re-render never re-triggers the load.
  const onTicketChangedRef: React.MutableRefObject<() => void> = useRef<() => void>(onTicketChanged);
  onTicketChangedRef.current = onTicketChanged;
  const onNotFoundRef: React.MutableRefObject<(() => void) | undefined> = useRef<(() => void) | undefined>(onNotFound);
  onNotFoundRef.current = onNotFound;

  // `quiet`: a refresh of a ticket already on screen. A failure then keeps what is shown instead of
  // replacing the conversation with an error.
  const load = useCallback(
    async (isActive: () => boolean, quiet: boolean = false): Promise<void> => {
      setLoading(true);
      if (!quiet) setLoadError(null);
      try {
        const [detail, thread] = await Promise.all([
          HelpdeskService.getTicket(ticketNumber),
          HelpdeskService.getMessages(ticketNumber)
        ]);
        if (!isActive()) return;
        setTicket(detail);
        setMessages(thread);
        // The pending copy has done its job once the thread holds the real message.
        setPendingReply((current: IHelpdeskPendingReply | null): IHelpdeskPendingReply | null =>
          current && isReplyInThread(current, thread) ? null : current
        );
        // Opening the ticket reads it; unlike Zendesk this is a separate call, not a side effect of GET.
        if (detail.hasUnreadReply) {
          await HelpdeskService.markRead(ticketNumber);
          if (isActive()) onTicketChangedRef.current();
        }
      } catch (error) {
        if (!isActive()) return;
        if (readHelpdeskError(error).status === 404 && onNotFoundRef.current) onNotFoundRef.current();
        else if (!quiet) setLoadError(readHelpdeskError(error).message);
      } finally {
        if (isActive()) setLoading(false);
      }
    },
    [ticketNumber]
  );

  useEffect((): (() => void) => {
    let active: boolean = true;
    setRestricted(false);
    load((): boolean => active);
    return (): void => {
      active = false;
    };
  }, [load]);

  // A change reported by the helpdesk stream (a reply, a status change, a deleted message): the
  // thread is re-read as a whole, so a deleted message simply disappears.
  const loadRef: React.MutableRefObject<typeof load> = useRef<typeof load>(load);
  loadRef.current = load;
  const isFirstRefresh: React.MutableRefObject<boolean> = useRef<boolean>(true);
  useEffect((): (() => void) | void => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false;
      return;
    }
    let active: boolean = true;
    loadRef.current((): boolean => active, true);
    return (): void => {
      active = false;
    };
  }, [refreshKey]);

  const reload = (): void => {
    load((): boolean => true, true);
    onTicketChangedRef.current();
  };

  const onThreadScroll = (): void => {
    const scroller: HTMLDivElement | null = scrollRef.current;
    if (!scroller) return;
    followsLatestRef.current =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= FOLLOW_LATEST_THRESHOLD_PX;
  };

  const onPendingChange = (reply: IHelpdeskPendingReply | null): void => {
    // One's own reply is always brought into view, wherever the reader had scrolled.
    if (reply) followsLatestRef.current = true;
    setPendingReply(reply);
  };

  const onReplySent = (messageId: number): void => {
    setPendingReply((current: IHelpdeskPendingReply | null): IHelpdeskPendingReply | null =>
      current ? { ...current, messageId, progress: null } : current
    );
    reload();
  };

  const ticketFiles: IHelpdeskFileTotals = useMemo(
    (): IHelpdeskFileTotals =>
      totalsOf(messages.flatMap((message: IHelpdeskMessage): IHelpdeskAttachment[] => message.attachments)),
    [messages]
  );

  const goToSurvey = (): void => {
    const survey: HTMLDivElement | null = surveyRef.current;
    if (!survey) return;
    survey.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "nearest" });
    // Keyboard and screen-reader users land on the survey instead of only seeing it scroll by.
    survey.focus({ preventScroll: true });
  };

  const openAttachment = async (attachment: IHelpdeskAttachment): Promise<void> => {
    // Open the tab inside the click so popup blockers allow it, then point it at the signed link.
    const tab: Window | null = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const link: IHelpdeskDownloadUrl = await HelpdeskService.getDownloadUrl(ticketNumber, attachment.id);
      // The link carries a short-lived grant for this one file only; previews exist for raster images.
      const target: string = link.previewUrl || link.url;
      if (tab) tab.location.href = target;
      else window.open(target, "_blank", "noopener,noreferrer");
    } catch (error) {
      tab?.close();
      KMainFunctions.displayErrorNotification(readHelpdeskError(error).message);
    }
  };

  if (presentation === "skeleton") return <HelpdeskTicketSkeleton />;
  // A fast load lands before the skeleton is due: hold the panel instead of flashing a placeholder.
  if (presentation === "pending") return <div className="kuika-helpdesk-viewer" />;

  if (loadError || !ticket) {
    return (
      <div className="kuika-helpdesk-viewer kuika-flex-center">
        <KuikaSmallText>{loadError ?? "The ticket could not be loaded."}</KuikaSmallText>
      </div>
    );
  }

  const context: string = contextLine(ticket.context);
  // The ITSM decides (contract §3.4): resolved, this person is the requester, and this resolution's
  // survey is not answered yet. Answering it reloads the ticket, which turns this off.
  const showSurvey: boolean = !restricted && ticket.surveyAvailable;
  const showComposer: boolean = !restricted && ticket.canReply;
  // Derived from the thread rather than cleared after it, so the copy and the real message are never
  // both drawn, and never neither.
  const pendingMessage: IHelpdeskMessage | null =
    pendingReply && !isReplyInThread(pendingReply, messages)
      ? pendingReplyMessage(pendingReply, ticket.requesterName)
      : null;

  return (
    <div className="kuika-helpdesk-viewer">
      <header className="kuika-helpdesk-viewer__header">
        <div className="kuika-flex-between">
          <div className="kuika-flex--gap-s kuika-align-center">
            <span className="kuika-helpdesk-viewer__reference">{ticket.referenceCode}</span>
            <HelpdeskStatusBadge
              status={ticket.status}
              statusGroup={ticket.statusGroup}
              zendeskStatus={ticket.zendeskStatus}
            />
          </div>
          <KuikaSmallText className="kuika-helpdesk-viewer__muted">{formatTicketDate(ticket.createdAt)}</KuikaSmallText>
        </div>
        <div data-testid="ticketSubject">
          <KuikaTextMedium>{ticket.subject}</KuikaTextMedium>
        </div>
        {context && (
          <KuikaSmallText data-testid="contextInfo" className="kuika-helpdesk-viewer__muted">
            {context}
          </KuikaSmallText>
        )}
        {showSurvey && (
          <KuikaInfoBox
            color="green"
            className="kuika-helpdesk-viewer__survey-prompt"
            leftUnit={
              <div className="kuika-flex--gap-xs kuika-align-center">
                <KuikaSvg name="circle-check" width={16} height={16} />
                <KuikaSmallText>This ticket is resolved. Let us know how it went.</KuikaSmallText>
              </div>
            }
            rightUnit={
              <KuikaButton data-testid="helpdeskSurveyButton" type="primary" size="sm" onClick={goToSurvey}>
                Take the survey
              </KuikaButton>
            }
          />
        )}
      </header>

      <div
        ref={scrollRef}
        className="kuika-helpdesk-viewer__scroll"
        data-testid="helpdeskThreadScroll"
        onScroll={onThreadScroll}
      >
        <HelpdeskMessageThread
          messages={messages}
          pendingMessage={pendingMessage}
          pendingStatus={pendingReply ? describePendingReply(pendingReply) : ""}
          onOpenAttachment={openAttachment}
        />
        {showSurvey && (
          <div ref={surveyRef} tabIndex={-1} className="kuika-helpdesk-viewer__survey">
            <HelpdeskSurvey ticketNumber={ticket.ticketNumber} onSubmitted={reload} />
          </div>
        )}
      </div>

      {(restricted || showComposer) && (
        <div className="kuika-helpdesk-viewer__footer">
          {restricted && <HelpdeskRestrictedNotice />}
          {showComposer && (
            <HelpdeskComposer
              ticketNumber={ticket.ticketNumber}
              limits={limits}
              ticketFiles={ticketFiles}
              onPendingChange={onPendingChange}
              onSent={onReplySent}
              onRestricted={(): void => setRestricted(true)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HelpdeskTicketViewer;
